/* eslint-disable no-param-reassign */
import { createSlice, createAsyncThunk, createEntityAdapter } from '@reduxjs/toolkit';

import { getApi, postApi, putApi } from 'lib/axios';
import { enqueueSnackbar } from 'stores/notificationSlice';
import { BASE_URL, USER_STATUS } from 'utils/const';

/**
 * 未承認ユーザー一覧を取得する
 */
export const fetchUnapprovedUsers = createAsyncThunk(
  'unapprovedUsers/fetchAll',
  async (data, { dispatch, rejectWithValue }) => {
    const param = {
      per_page: data.rowsPerPage,
      page: data.page,
      'statuses[]': String(USER_STATUS.APPROVAL_PENDING),
    };

    try {
      const res = await getApi(`${BASE_URL}/users`, param);

      return { res };
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: 'エラーが発生しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      return rejectWithValue(error?.response?.data?.message || 'エラーが発生しました');
    }
  },
);

/**
 * ユーザーを承認する
 */
export const approveUser = createAsyncThunk(
  'unapprovedUsers/approve',
  async ({ userId }, { dispatch, rejectWithValue }) => {
    try {
      const res = await putApi(`${BASE_URL}/users/${userId}/approve`, {});
      dispatch(
        enqueueSnackbar({
          message: 'ユーザーを承認しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return { userId, res };
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: '承認処理に失敗しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      return rejectWithValue(error?.response?.data?.message || '承認処理に失敗しました');
    }
  },
);

/**
 * ユーザーを否認する
 */
export const rejectUser = createAsyncThunk(
  'unapprovedUsers/reject',
  async ({ userId, adminReason, adminComment }, { dispatch, rejectWithValue }) => {
    try {
      const res = await postApi(`${BASE_URL}/users/${userId}/withdrawal`, {
        admin_reason: adminReason,
        admin_comment: adminComment,
      });
      dispatch(
        enqueueSnackbar({
          message: 'ユーザーを否認しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'success',
          },
        }),
      );

      return { userId, res };
    } catch (error) {
      dispatch(
        enqueueSnackbar({
          message: '否認処理に失敗しました。',
          options: {
            key: new Date().getTime() + Math.random(),
            variant: 'error',
          },
        }),
      );

      return rejectWithValue(error?.response?.data?.message || '否認処理に失敗しました');
    }
  },
);

const unapprovedUsersAdapter = createEntityAdapter({
  selectId: (user) => user.user_id,
});

const unapprovedUserSlice = createSlice({
  name: 'unapprovedUsers',

  initialState: unapprovedUsersAdapter.getInitialState({
    loading: false,
    total: 0,
  }),

  reducers: {},

  extraReducers: (builder) => {
    builder
      .addCase(fetchUnapprovedUsers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUnapprovedUsers.fulfilled, (state, action) => {
        state.loading = false;
        unapprovedUsersAdapter.setAll(state, action.payload.res.data);
        state.total = action.payload.res.meta?.total || action.payload.res.data.length;
      })
      .addCase(fetchUnapprovedUsers.rejected, (state) => {
        state.loading = false;
      })
      .addCase(approveUser.fulfilled, (state, action) => {
        unapprovedUsersAdapter.removeOne(state, action.payload.userId);
        state.total -= 1;
      })
      .addCase(rejectUser.fulfilled, (state, action) => {
        unapprovedUsersAdapter.removeOne(state, action.payload.userId);
        state.total -= 1;
      });
  },
});

export const unapprovedUserSelectors = unapprovedUsersAdapter.getSelectors(
  (state) => state.unapprovedUsers,
);

export default unapprovedUserSlice.reducer;
